import React, { useState } from 'react';
import useIntegratePOSHeartlandRetailStore from '../../../stores/integratePOSHeartlandRetailStore';
import { Checkbox, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Heartland Retail Prerequisites Content
export const HeartlandRetailPrerequisitesContent = ({ onNext, onBack, onClose }) => {
  const { setHeartlandAccountVerified } = useIntegratePOSHeartlandRetailStore();

  const [completedSteps, setCompletedSteps] = useState({
    verifyAccount: false,
    checkSubscription: false,
    confirmPermissions: false,
    readyToSetup: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setHeartlandAccountVerified(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Heartland Retail Integration Setup
        </h2>
        <p className="text-gray-600">
          Let's prepare for your Heartland Retail POS integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Prerequisites for Heartland Retail Integration</h3>

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.verifyAccount}
              onChange={(e) => handleStepChange('verifyAccount', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Verify Heartland Retail Account</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Ensure you have an active <span className="font-medium">Heartland Retail</span> account with API access enabled.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    You should be able to log into your Heartland Retail Portal at <span className="font-mono text-sm">portal.heartlandretail.com</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.checkSubscription}
              onChange={(e) => handleStepChange('checkSubscription', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Check API Access Plan</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Confirm that your Heartland Retail subscription includes <span className="font-medium">API access</span> and third-party integrations.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    Basic retail plans may not include advanced API functionality for inventory and sales data.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.confirmPermissions}
              onChange={(e) => handleStepChange('confirmPermissions', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Confirm Store Manager Permissions</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Verify that your account has <span className="font-medium">Store Manager</span> or <span className="font-medium">Administrator</span> level permissions.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    These permissions are required to generate API keys and manage integrations for your retail locations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.readyToSetup}
              onChange={(e) => handleStepChange('readyToSetup', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">4.</span>
                <div>
                  <p className="font-medium text-gray-900">Ready for API Setup</p>
                  <p className="text-gray-600 text-sm mt-1">
                    I'm ready to navigate to the Heartland Retail Portal and generate API credentials for Rightwork integration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to API Setup
        </button>
      </div>
    </div>
  );
};

// Heartland Retail API Setup Content
export const HeartlandRetailAPISetupContent = ({ onNext, onBack, onClose }) => {
  const { setAPISetupCompleted } = useIntegratePOSHeartlandRetailStore();

  const [completedSteps, setCompletedSteps] = useState({
    navigateToPortal: false,
    accessAPISection: false,
    generateApplication: false,
    saveCredentials: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setAPISetupCompleted(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Generate Heartland Retail API Credentials
        </h2>
        <p className="text-gray-600">
          Set up API access in your Heartland Retail Portal
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Generate Heartland Retail API Token</h3>

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.navigateToPortal}
              onChange={(e) => handleStepChange('navigateToPortal', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Log into Heartland Retail</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Go to <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">portal.heartlandretail.com</span> and log in
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.accessAPISection}
              onChange={(e) => handleStepChange('accessAPISection', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Access API Tokens</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Click your name in the top right corner → <span className="font-medium">My Account</span> → <span className="font-medium">API</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.generateApplication}
              onChange={(e) => handleStepChange('generateApplication', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Generate New Token</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Click <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Generate new token</span></p>
                    <p className="mt-1.5">Enter description: <span className="font-mono bg-white border px-2 py-1 rounded text-sm">Rightwork Integration</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.saveCredentials}
              onChange={(e) => handleStepChange('saveCredentials', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">4.</span>
                <div>
                  <p className="font-medium text-gray-900">Copy Your API Token</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Click <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Generate Token</span> → Copy the token immediately</p>
                    <p className="text-amber-700 mt-1 font-medium">⚠️ Copy now - you won't be able to see it again!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Credential Input
        </button>
      </div>
    </div>
  );
};

// Heartland Retail Credential Input Content
export const HeartlandRetailCredentialInputContent = ({ onBack, onClose, companyId, onIntegrationComplete }) => {
  const { setFormData, formData, integrateHeartlandRetail } = useIntegratePOSHeartlandRetailStore();

  const [credentials, setCredentials] = useState({
    secretKey: formData.secretKey || ''
  });
  const [showSecret, setShowSecret] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const allFieldsCompleted = credentials.secretKey.trim().length > 0;

  const handleCredentialChange = (field, value) => {
    const updatedCredentials = {
      ...credentials,
      [field]: value
    };
    setCredentials(updatedCredentials);
    setFormData(field, value);
  };

  const handleComplete = async () => {
    if (allFieldsCompleted && !isLoading) {
      setIsLoading(true);
      try {
        const result = await integrateHeartlandRetail(companyId);

        if (result.success) {
          if (typeof onIntegrationComplete === 'function') {
            try {
              await onIntegrationComplete();
            } catch (refreshError) {
              console.error('Error refreshing integration data:', refreshError);
            }
          }
          alert('Heartland Retail integration completed successfully!');
          onClose();
        } else {
          alert(result.error || 'There was an error setting up the integration. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Error setting up Heartland Retail integration:', error);
        alert('There was an error setting up the integration. Please try again or contact support.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Enter Heartland Retail API Credentials
        </h2>
        <p className="text-gray-600">
          Input your Heartland Retail secret key to complete the integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-4">Enter Your API Token</h3>

        <div className="space-y-4 text-sm text-gray-700 mb-6">
          <p>Enter the API token you just generated from your Heartland Retail Portal.</p>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">Need help finding your API token?</h4>
            <p className="text-sm">Go to your Heartland Retail Portal → Your Name → My Account → API → API Tokens page.</p>
          </div>
        </div>

        <div>
          <label htmlFor="secret-key" className="block text-sm font-medium text-gray-700 mb-2">
            API Token <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <TextField
              id="secret-key"
              fullWidth
              variant="outlined"
              size="small"
              type={showSecret ? "text" : "password"}
              value={credentials.secretKey}
              onChange={(e) => handleCredentialChange('secretKey', e.target.value)}
              placeholder="Enter your API token..."
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  paddingRight: "40px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
            >
              {showSecret ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            This is your API token from Heartland Retail
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold">Retail vs Restaurant:</p>
            <p>Make sure you're using <span className="font-medium">Heartland Retail</span> credentials, not Heartland Restaurant. The two systems have different API endpoints and data structures.</p>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-green-800">
            <p className="font-semibold">Security:</p>
            <p>Your secret key is encrypted and stored securely. Rightwork follows industry best practices for API security and retail data protection.</p>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleComplete}
          disabled={!allFieldsCompleted || isLoading}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allFieldsCompleted && !isLoading
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Completing...
            </div>
          ) : (
            'Complete Integration'
          )}
        </button>
      </div>
    </div>
  );
};
